
import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TipFormFields } from "./tip-form/TipFormFields";
import { TipFormActions } from "./tip-form/TipFormActions";

interface TipFormProps {
  initialTitle?: string;
  initialContent?: string;
  initialVotes?: number;
  initialStatus?: string;
  initialEmoji?: string;
  initialUniversityId?: number | null;
  onSubmit: (data: { 
    title: string; 
    content: string; 
    votes: number; 
    status: string; 
    emoji: string;
    universityId: number | null;
  }) => Promise<void>;
  onDelete?: () => Promise<void>;
  error: string | null;
  mode: 'add' | 'edit';
}

export function TipForm({ 
  initialTitle = "", 
  initialContent = "",
  initialVotes = 0, 
  initialStatus = "draft",
  initialEmoji = "❤️",
  initialUniversityId = null,
  onSubmit, 
  onDelete,
  error, 
  mode 
}: TipFormProps) {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [votes, setVotes] = useState(initialVotes);
  const [status, setStatus] = useState(initialStatus);
  const [emoji, setEmoji] = useState(initialEmoji);
  const [universityId, setUniversityId] = useState<number | null>(initialUniversityId);

  useEffect(() => {
    setTitle(initialTitle);
    setContent(initialContent);
    setVotes(initialVotes);
    setStatus(initialStatus);
    setEmoji(initialEmoji);
    setUniversityId(initialUniversityId);
  }, [initialTitle, initialContent, initialVotes, initialStatus, initialEmoji, initialUniversityId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({ title, content, votes, status, emoji, universityId });
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{mode === 'add' ? 'Add New Tip' : 'Edit Tip'}</DialogTitle>
      </DialogHeader>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <TipFormFields
          title={title}
          content={content}
          emoji={emoji}
          votes={votes}
          status={status}
          universityId={universityId}
          mode={mode}
          onTitleChange={setTitle}
          onContentChange={setContent}
          onEmojiChange={setEmoji}
          onVotesChange={setVotes}
          onStatusChange={setStatus}
          onUniversityChange={setUniversityId}
        />
        <TipFormActions mode={mode} onDelete={onDelete} />
      </form>
    </DialogContent>
  );
}
